import React, { useRef, useEffect, useState } from "react";
import { Box, Grid, Button, Typography, IconButton, alpha } from "@mui/material";
import { Phone, Backspace } from "@mui/icons-material";
import { dialPad } from "../const/dialPad";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";

const MAX_DIGITS = 10;

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const formatUSLive = (digits: string) => {
  const d = digits.slice(0, MAX_DIGITS);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/* ===================== MAIN COMPONENT ===================== */

export const DialerCall = () => {
  const { theme } = useContactCenterTheme();
  const [phoneNumber, setPhoneNumber] = useState("");
  const { makeCall, status } = useVirtualAssistant();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /* ---------- INPUT HANDLERS ---------- */

  const handleDigit = (digit: string) => {
    setPhoneNumber((prev) => {
      if (prev.length >= MAX_DIGITS) return prev;
      return prev + digit;
    });
    inputRef.current?.focus();
  };

  const handleBackspace = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = onlyDigits(e.target.value).slice(0, MAX_DIGITS);
    setPhoneNumber(digits);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && phoneNumber.length === MAX_DIGITS && status === "ready") {
      await makeCall(`+1${phoneNumber}`);
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleCall = async () => {
    if (phoneNumber.length !== MAX_DIGITS || status !== "ready") return;
    await makeCall(`+1${phoneNumber}`);
  };

  /* ===================== RENDER ===================== */

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      onClick={handleContainerClick}
      sx={{ bgcolor: theme.shell.bg, color: theme.shell.textPrimary, height: "100%", cursor: "text", py: 2 }}
    >
      <Box sx={{ width: "100%", maxWidth: 320 }}>
        <input
          ref={inputRef}
          type="text"
          value={phoneNumber}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          style={{
            position: "absolute",
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: "none",
          }}
        />


        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              minHeight: 36,
              color: phoneNumber ? theme.shell.accent : theme.chat.dividerText,
              fontWeight: 700,
              fontSize: { xs: "1.60rem", sm: "2rem" },
              letterSpacing: 1.5,
              marginTop: { xs: 5, sm: 0 },
            }}
          >
            {phoneNumber ? formatUSLive(phoneNumber) : "Enter number"}
          </Typography>
        </Box>

        {/* Dial pad */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {dialPad.map(({ digit, letters }) => (
            <Grid item xs={4} key={digit}>
              <Button
                onClick={() => handleDigit(digit)}
                sx={{
                  width: "100%",
                  height: { xs: 40, sm: 62, xl: 62 },
                  borderRadius: "50%",
                  display: "flex",
                  overflow: { xs: "hidden", sm: "visible" },
                  flexDirection: "column",
                  justifyContent: "center",
                  color: theme.shell.accent,
                  minWidth: 0,
                  p: 0,
                  "&:hover": { bgcolor: alpha(theme.shell.accent, 0.08) },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.5rem" }}>
                  {digit}
                </Typography>
                {letters && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.shell.textSecondary,
                      fontWeight: 600,
                      fontSize: "0.6rem",
                      letterSpacing: "0.08em",
                      mt: -0.5,
                    }}
                  >
                    {letters}
                  </Typography>
                )}
              </Button>
            </Grid>
          ))}
        </Grid>

        {/* Actions */}
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={4} />

          <Grid item xs={4}>
            <Button
              onClick={handleCall}
              disabled={phoneNumber.length !== MAX_DIGITS || status !== "ready"}
              variant="contained"
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                minWidth: 0,
                ml: 3,
                my: 1,
                bgcolor: "#4CAF50",
                "&:hover": { bgcolor: "#45a049" },
                "&.Mui-disabled": { bgcolor: theme.chat.scrollThumb },
              }}
            >
              <Phone sx={{ fontSize: 28, color: "white" }} />
            </Button>
          </Grid>

          <Grid item xs={4}>
            <IconButton
              onClick={handleBackspace}
              disabled={!phoneNumber}
              sx={{ width: 56, height: 56, color: theme.shell.textPrimary }}
            >
              <Backspace sx={{ fontSize: 22 }} />
            </IconButton>
          </Grid>
        </Grid>

        {/* Status */}
        <Box sx={{ mt: 1.5, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: status === "ready" ? "success.main" : theme.shell.textSecondary,
            }}
          >
            {status === "ready" ? "Ready • Press Enter" : "Connecting..."}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};